"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const categories = [{
  title: "Full Planning",
  description: "A comprehensive journey from the first concept to the final dance, ensuring every detail is meticulously curated for your vision.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683483/img/wedding_full_planning.png",
  link: "/CuratedServicesPage"
}, {
  title: "Design & Styling",
  description: "Transforming spaces into ethereal landscapes with bespoke floral arrangements, lighting, and architectural decor elements.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683483/img/wedding_design_styling.png",
  link: "/CuratedServicesPage"
}, {
  title: "Day-of Coordination",
  description: "Seamless execution of your carefully laid plans, allowing you to be fully present in every romantic moment of your celebration.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683483/img/wedding_coordination.png",
  link: "/CuratedServicesPage"
}, {
  title: "Bespoke Experiences",
  description: "Customized additions from welcome soirées to farewell brunches, creating a multi-day celebration of your unique love story.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683483/img/wedding_bespoke_events.png",
  link: "/CuratedServicesPage"
}];
function CategoryTile({
  category,
  index
}: {
  category: typeof categories[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <Link to={category.link} data-index={index} ref={ref as React.RefObject<HTMLAnchorElement>} className={`group relative block aspect-[3/4] w-full overflow-hidden bg-muted transition-all duration-1000 ease-out shadow-md rounded-md ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
      {}
      <img src={category.image} alt={category.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
      
      {}
      <div className="absolute inset-0 bg-black/20 transition-colors duration-700 ease-out group-hover:bg-black/50" />

      {}
      <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10">
        <div className="translate-y-8 transition-transform duration-500 ease-out group-hover:translate-y-0">
          <h3 className="mb-4 font-default text-3xl font-medium text-white drop-shadow-lg md:text-4xl tracking-tight">
            {category.title}
          </h3>
          <p className="font-sans text-base text-white/95 opacity-0 drop-shadow-md transition-opacity delay-100 duration-500 ease-out group-hover:opacity-100 md:text-lg">
            {category.description}
          </p>
        </div>
      </div>
    </Link>;
}
export default function ExquisitePlanningDesign() {
  return <section data-section-id="223402" id="exquisite-planning-design" className="relative bg-background py-24 md:py-32 text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col gap-16 lg:flex-row lg:gap-24">
          
          {}
          <div className="lg:w-1/3">
            <div className="sticky top-32 flex flex-col items-start">
              <span className="mb-4 font-mono text-sm font-medium uppercase tracking-widest text-secondary">
                Curated Services
              </span>
              <h2 className="mb-6 font-default text-4xl font-light leading-tight text-foreground md:text-5xl lg:text-6xl tracking-tight">
                Exquisite Planning & Design
              </h2>
              <p className="mb-8 font-sans text-lg text-muted-foreground leading-relaxed">
                At VOW, we believe every wedding should be as unique as the couple it celebrates. Our dedicated event team provides full-service planning and personalized design options to reflect your vision.
              </p>
              <Button asChild variant="outline" size="lg" className="rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 font-mono uppercase tracking-widest text-xs">
                <Link to="/CuratedServicesPage">
                  Explore All Services
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:w-2/3 lg:gap-8">
            <div className="flex flex-col gap-6 lg:gap-8 lg:pt-24">
              {categories.filter((_, i) => i % 2 === 0).map((category, i) => <CategoryTile key={category.title} category={category} index={i * 2} />)}
            </div>
            <div className="flex flex-col gap-6 lg:gap-8">
              {categories.filter((_, i) => i % 2 !== 0).map((category, i) => <CategoryTile key={category.title} category={category} index={i * 2 + 1} />)}
            </div>
          </div>

        </div>
      </div>
    </section>;
}